/**
 * The Map tab on a phone — `docs/property-maps-v2.md` §6.1, board 8.
 *
 * **No MapLibre here, and no tiles.** §6.1 is explicit: the tab is plain
 * cards, so it is fast on a bad connection and the library is only fetched
 * when the editor opens. The mockup draws a thumbnail; the spec says not to
 * load one, and the spec wins where the two disagree — a card that costs
 * twenty tile requests to render is the opposite of what this screen is for.
 *
 * With no maps: the heading and two full-width cards, **the whole card
 * tappable** rather than a button inside it, because a 358px card with a
 * 44px button in it wastes the other 300px of target.
 *
 * With maps: a card per map — name, **Switch**, a row per layer, and the
 * measurements line. The green action in the quick-action row is `Open map`,
 * which the record page supplies (§2.6); this screen does not draw its own
 * primary, so there is never two.
 */
import { cn } from '../../lib/cn.js'
import { LAYER_PALETTE } from '@rowted/shared'
import { measurementText } from '../measurement-text.js'
import { layerWords, shapeWords } from '../words.js'
import { IconChevronRight, IconMaps, IconRuler } from '../../frame/icons.js'
import { AudienceChip, type Division } from '../sharing/audience-chip.js'
import type { MapLayer, MapSummary, MeasurementGroup } from '../api.js'

export function PhoneMapTab({
  propertyName, maps, measurements, openId, openLayers, divisions, canEdit, offerMeasuring,
  onCreate, onMeasure, onOpen, onSeeMeasurements,
}: {
  propertyName: string
  maps: MapSummary[]
  measurements: MeasurementGroup[]
  /** Which map the green **Open map** action would open. */
  openId: string | null
  /** That map's layers, when they have arrived. The others show a summary. */
  openLayers: { layers: MapLayer[]; shapes: Record<string, number> } | null
  divisions: Division[]
  canEdit: boolean
  offerMeasuring: boolean
  onCreate: () => void
  onMeasure: () => void
  onOpen: (mapId: string) => void
  onSeeMeasurements: () => void
}) {
  const live = maps.filter((map) => !map.archived)
  const measured = measurements.filter((group) => group.total > 0)
  const savedParts = measurements.reduce((count, group) => count + group.parts.length, 0)

  if (live.length === 0) {
    return (
      <div className="flex flex-col gap-2.5 px-4 py-3">
        <div className="flex flex-col gap-[3px]">
          <b className="text-[19px] font-bold tracking-[-0.01em]">No maps yet</b>
          <span className="text-[13.5px] text-text-muted">
            Pick how you want to start. You can do the other later.
          </span>
        </div>

        {canEdit && (
          <>
            <BigChoice
              ringed
              icon={<IconMaps size={24} />}
              title="Create a new map"
              body={'Name it, then add layers — zones, routes, hazards. Share each with all'
                + ' crews or some divisions.'}
              onClick={onCreate}
            />
            {offerMeasuring && (
              <BigChoice
                icon={<IconRuler size={24} />}
                title="Just measure the property"
                body="Outline areas for sq ft and lin ft. Save the numbers to the property."
                onClick={onMeasure}
              />
            )}
          </>
        )}

        {!canEdit && (
          <p className="m-0 text-body text-text-muted">
            No one has made a map for {propertyName} yet.
          </p>
        )}

        {measured.length > 0 && (
          <button
            type="button"
            onClick={onSeeMeasurements}
            className="text-left text-cell text-text-muted"
          >
            {'Already measured: '}
            {measured.map(measurementText).join(' · ')}
            <span className="ml-1 font-semibold text-accent">See in Overview ›</span>
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 px-4 py-3">
      {live.map((map) => {
        const mine = openLayers && map.id === openId ? openLayers : null
        return (
          <div key={map.id} className="overflow-hidden rounded-record border border-line bg-surface">
            {/*
              * The name row opens this map. The mockup draws **Switch** here,
              * which belongs with the switcher sheet (§5.6, PR 5) — and on
              * this screen every map already has its own card, so a control
              * that opens a list of the cards you are looking at would be a
              * control that does nothing.
              */}
            <button
              type="button"
              onClick={() => onOpen(map.id)}
              className="flex w-full items-center px-4 pb-2.5 pt-3 text-left"
            >
              <b className="text-[15px] font-bold">{map.name}</b>
              <span className="ml-auto text-body font-semibold text-accent">Open</span>
            </button>

            {mine
              ? [...mine.layers].sort((a, b) => a.sortOrder - b.sortOrder).map((layer) => (
                <div
                  key={layer.id}
                  className={cn(
                    'flex min-h-[52px] items-center gap-2.5 border-t border-line-faint px-4',
                    'text-cell',
                  )}
                >
                  <span
                    aria-hidden
                    className="h-3 w-3 shrink-0 rounded-tile-small"
                    style={{ background: LAYER_PALETTE[layer.color] }}
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-2">
                    <b className="truncate font-semibold">{layer.name}</b>
                    <AudienceChip
                      audience={layer.audience}
                      divisionIds={layer.divisionIds}
                      divisions={divisions}
                      className="min-w-0 truncate text-sub"
                    />
                  </div>
                  <span className="shrink-0 text-sub text-text-faint">
                    {shapeWords(mine.shapes[layer.id] ?? 0)}
                  </span>
                </div>
              ))
              : (
                <button
                  type="button"
                  onClick={() => onOpen(map.id)}
                  className={cn(
                    'flex w-full min-h-[52px] items-center gap-2 border-t border-line-faint',
                    'px-4 text-left text-cell text-text-muted',
                  )}
                >
                  {layerWords(map.layerCount)}
                  {' · '}
                  {shapeWords(map.shapeCount)}
                  {map.audienceSummary.length > 0 && ` · ${map.audienceSummary.join(' · ')}`}
                  <span className="ml-auto text-text-faint"><IconChevronRight size={16} /></span>
                </button>
              )}

            <button
              type="button"
              onClick={onSeeMeasurements}
              className={cn(
                'flex w-full min-h-[52px] items-center gap-2 border-t border-line-faint px-4',
                'text-left text-cell text-text-muted',
              )}
            >
              <IconRuler size={14} className="shrink-0" />
              {/* The count never wraps and never truncates; the totals beside
                  it are the only part allowed to lose characters, the same
                  rule a phone list's card line follows (v3 §8). */}
              <span className="shrink-0 whitespace-nowrap">
                {'Measurements'}
                {/* §5.6's count is of *saved parts*, and a property measured by
                    typing a total has none — so a nought beside two real
                    numbers is an answer somebody would act on. The count is
                    dropped where there is nothing to count, the same rule the
                    tab badge and a card line's `$0` follow. */}
                {savedParts > 0 && ` · ${savedParts}`}
              </span>
              {measured.length > 0 && (
                <span className="min-w-0 truncate">
                  {`· ${measured.map(measurementText).join(' · ')}`}
                </span>
              )}
              <span className="ml-auto shrink-0 font-semibold text-accent">See all</span>
            </button>
          </div>
        )
      })}
    </div>
  )
}

/** A whole-card target: 88px minimum, which is well past the 44px rule. */
function BigChoice({
  icon, title, body, onClick, ringed,
}: {
  icon: React.ReactNode
  title: string
  body: string
  onClick: () => void
  ringed?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex min-h-[88px] w-full items-center gap-3.5 rounded-dialog border bg-surface p-3.5',
        'text-left',
        ringed ? 'border-[1.5px] border-accent-line ring-2 ring-accent-ring' : 'border-line',
      )}
    >
      <span className={cn(
        'inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px]',
        ringed ? 'bg-accent-soft text-accent' : 'bg-surface-group text-text-body',
      )}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <b className="text-[16px] font-bold">{title}</b>
        <span className="text-[13px] leading-[1.4] text-text-muted">{body}</span>
      </span>
      <span className="shrink-0 text-text-faint"><IconChevronRight size={18} /></span>
    </button>
  )
}
